import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { gitApi } from '../api/gitApi';

/**
 * A 400 from this endpoint comes from `resolve_git_auth` and already has a
 * user-facing `detail` (e.g. "Connect the server's login, or add a personal
 * access token."). Surface it as the error message instead of axios's
 * generic "Request failed with status code 400" so `ProjectSelector` can
 * render it as-is.
 */
function extractErrorDetail(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return null;
}

export function useGitProjects(
  serverId: string | null,
  search: string,
  page: number,
  perPage: number,
) {
  return useQuery({
    queryKey: ['gitProjects', serverId, search, page, perPage],
    queryFn: async () => {
      if (!serverId) throw new Error('serverId required');
      try {
        return await gitApi.listProjects(serverId, search || null, page, perPage);
      } catch (err) {
        const detail = extractErrorDetail(err);
        throw detail ? new Error(detail) : err;
      }
    },
    enabled: Boolean(serverId),
  });
}
